import qz from 'qz-tray';
import axios from 'axios';
import { API_BASE_URL, IMPRESION_FIRMAR, FACTURACION_GET } from './apiConfig';

// Servicio de impresión de comandas (ticket no fiscal) vía QZ Tray. Best-effort
// en todo momento: ningún llamado de acá debe frenar el flujo de una venta —
// si la impresora/QZ Tray no está disponible, se resuelve con {ok:false}.

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtFechaTicket = (f) => {
  const d = f instanceof Date ? f : new Date(String(f || '').replace(' ', 'T'));
  if (isNaN(d)) return String(f || '');
  return `${d.toLocaleDateString('es-AR')} ${d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

let seguridadConfigurada = false;

// QZ Tray firma cada pedido de impresión para no mostrar un cartel de permiso
// en cada ticket. El certificado público lo trae la config global (facturacionget.php);
// la firma la hace el backend (impresionfirmar.php), la clave privada nunca sale del servidor.
function configurarSeguridad(certificadoPublico) {
  if (seguridadConfigurada) return;
  seguridadConfigurada = true;

  // El backend firma con OPENSSL_ALGO_SHA512 (impresionfirmar.php); qz-tray.js
  // por defecto verifica con SHA1 si no se le avisa lo contrario, lo que hace
  // que la firma salga "Invalid Signature" y pregunte permiso en cada venta.
  qz.security.setSignatureAlgorithm('SHA512');

  qz.security.setCertificatePromise((resolve, reject) => {
    if (certificadoPublico) resolve(certificadoPublico);
    else reject(new Error('No hay un certificado de firma generado (Configuración > Comandera).'));
  });

  qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
    axios.post(API_BASE_URL + IMPRESION_FIRMAR, { toSign }, { headers: authHeader() })
      .then((res) => {
        if (res.data?.success) resolve(res.data.data.signature);
        else reject(new Error(res.data?.message || 'Error al firmar el pedido de impresión.'));
      })
      .catch((e) => reject(e));
  });
}

// Timeout propio: si el navegador bloquea/no responde al intento de conexión
// a QZ Tray (ej. Chrome pide permiso de "acceso a red privada" para hablarle
// a localhost y el usuario lo bloquea, o el diálogo queda sin responder),
// qz-tray.js puede quedar esperando un evento de la conexión que nunca llega.
// Sin este timeout, imprimirTicketSilencioso() podía quedar pendiente para
// siempre en ese navegador (en otros, como Edge, el permiso ya está
// otorgado y conecta sin problema).
function conTimeout(promesa, ms, mensaje) {
  return Promise.race([
    promesa,
    new Promise((_, reject) => setTimeout(() => reject(new Error(mensaje)), ms)),
  ]);
}

async function asegurarConexion(certificadoPublico) {
  configurarSeguridad(certificadoPublico);
  if (!qz.websocket.isActive()) {
    await conTimeout(
      qz.websocket.connect({ retries: 1, delay: 1 }),
      8000,
      'No se pudo conectar con QZ Tray (tiempo de espera agotado). Si tu navegador pidió permiso para acceder a la red local, aceptalo e intentá de nuevo.'
    );
  }
}

// Busca las impresoras que ve QZ Tray en esta PC (requiere QZ Tray corriendo).
export async function buscarImpresoras(certificadoPublico) {
  await asegurarConexion(certificadoPublico);
  return qz.printers.find();
}

export async function qzConectado(certificadoPublico) {
  try {
    await asegurarConexion(certificadoPublico);
    return true;
  } catch {
    return false;
  }
}

// Códigos de página ESC/POS por marca (ambas son ESC/POS estándar; lo único
// que puede variar es la página de códigos para que tildes/ñ salgan bien).
const CODEPAGE_POR_MARCA = { epson: 16, hasar: 2 };

// El texto se manda tal cual QZ Tray lo pasa a la impresora en modo 'plain':
// cada carácter cuyo código sea <= 255 se envía como un byte. Fuera de ese
// rango (emoji, etc.) se reemplaza para no romper el ticket.
const aBytesImprimibles = (s) => String(s ?? '').split('').map((ch) => (ch.codePointAt(0) <= 0xFF ? ch : '?')).join('');

const formatearFila = (izq, der, columnas) => {
  izq = aBytesImprimibles(izq);
  der = aBytesImprimibles(der);
  if (izq.length + der.length + 1 > columnas) {
    izq = izq.slice(0, Math.max(0, columnas - der.length - 1));
  }
  const espacios = Math.max(1, columnas - izq.length - der.length);
  return izq + ' '.repeat(espacios) + der;
};

/**
 * Arma el comando ESC/POS completo del ticket.
 * venta: { fecha, sucursalNombre, sucursalDireccion, vendedor, items:[{descripcion,cantidad,precio_unitario,subtotal}], subtotal, total, observaciones, pagaCon, cambio, descuentoTipo, descuentoValor, descuentoMonto }
 * sucursalImpresion: { impresion_marca, impresion_ancho_papel, impresion_cortar_papel, impresion_nombre_fantasia, impresion_texto_pie }
 * El nombre de fantasía y el pie del ticket son configurables POR SUCURSAL
 * (Configuración > Comandera) — nunca se toman de la config global/fiscal.
 */
export function construirTicketEscPos(venta, sucursalImpresion, opcionesGlobales = {}) {
  const ESC = String.fromCharCode(0x1B);
  const GS = String.fromCharCode(0x1D);
  const init = ESC + '@';
  const codepage = ESC + 't' + String.fromCharCode(CODEPAGE_POR_MARCA[sucursalImpresion?.impresion_marca] ?? 16);
  const alinearCentro = ESC + 'a' + String.fromCharCode(1);
  const alinearIzq = ESC + 'a' + String.fromCharCode(0);
  const negritaOn = ESC + 'E' + String.fromCharCode(1);
  const negritaOff = ESC + 'E' + String.fromCharCode(0);
  const cortar = GS + 'V' + String.fromCharCode(0);

  const columnas = Number(sucursalImpresion?.impresion_ancho_papel) === 58 ? 32 : 48;
  const linea = '-'.repeat(columnas);

  let out = init + codepage + alinearCentro;
  if (sucursalImpresion?.impresion_nombre_fantasia) out += negritaOn + aBytesImprimibles(sucursalImpresion.impresion_nombre_fantasia) + negritaOff + '\n';
  if (venta.sucursalDireccion) out += aBytesImprimibles(venta.sucursalDireccion) + '\n';
  out += linea + '\n';

  out += alinearIzq;
  out += `${fmtFechaTicket(venta.fecha)}${venta.id ? ` #${venta.id}` : ''}\n`;
  out += linea + '\n';

  (venta.items || []).forEach((it) => {
    out += aBytesImprimibles(it.descripcion) + '\n';
    out += formatearFila(`  ${it.cantidad} x ${fmtMoneda(it.precio_unitario)}`, fmtMoneda(it.subtotal), columnas) + '\n';
  });
  out += linea + '\n';

  if (venta.descuentoMonto > 0) {
    out += formatearFila('Subtotal', fmtMoneda(venta.subtotal), columnas) + '\n';
    const etiquetaDescuento = venta.descuentoTipo === 'porcentaje' ? `Descuento (${venta.descuentoValor}%)` : 'Descuento';
    out += formatearFila(etiquetaDescuento, `-${fmtMoneda(venta.descuentoMonto)}`, columnas) + '\n';
  }
  out += negritaOn + formatearFila('TOTAL', fmtMoneda(venta.total), columnas) + negritaOff + '\n';
  if (venta.pagaCon != null && venta.pagaCon !== '') {
    out += formatearFila('Paga con', fmtMoneda(venta.pagaCon), columnas) + '\n';
    out += formatearFila('Cambio', fmtMoneda(venta.cambio), columnas) + '\n';
  }
  if (venta.observaciones) out += '\n' + aBytesImprimibles(venta.observaciones) + '\n';

  out += '\n' + alinearCentro;
  if (sucursalImpresion?.impresion_texto_pie) out += aBytesImprimibles(sucursalImpresion.impresion_texto_pie) + '\n';
  out += '\n\n\n';

  if (sucursalImpresion?.impresion_cortar_papel) out += cortar;

  return out;
}

// Venta de prueba para la vista previa de "Configuración > Comandera" — un
// producto de ejemplo, no una venta real.
export function ventaDePrueba(sucursalNombre) {
  return {
    id: 123,
    fecha: new Date(),
    sucursalNombre,
    sucursalDireccion: '',
    items: [{ descripcion: 'Producto de prueba', cantidad: 2, precio_unitario: 1500, subtotal: 3000 }],
    subtotal: 3000,
    total: 3000,
    observaciones: '',
    pagaCon: null,
    descuentoMonto: 0,
  };
}

// Mismo layout que construirTicketEscPos, pero en texto plano (sin códigos
// ESC/POS) para poder mostrarlo en pantalla como vista previa.
export function construirTicketPreviewTexto(venta, sucursalImpresion) {
  const columnas = Number(sucursalImpresion?.impresion_ancho_papel) === 58 ? 32 : 48;
  const linea = '-'.repeat(columnas);
  const centrar = (s) => {
    s = String(s ?? '');
    if (s.length >= columnas) return s;
    return ' '.repeat(Math.floor((columnas - s.length) / 2)) + s;
  };

  const out = [];
  if (sucursalImpresion?.impresion_nombre_fantasia) out.push(centrar(sucursalImpresion.impresion_nombre_fantasia));
  if (venta.sucursalDireccion) out.push(centrar(venta.sucursalDireccion));
  out.push(linea);

  out.push(`${fmtFechaTicket(venta.fecha)}${venta.id ? ` #${venta.id}` : ''}`);
  out.push(linea);

  (venta.items || []).forEach((it) => {
    out.push(it.descripcion);
    out.push(formatearFila(`  ${it.cantidad} x ${fmtMoneda(it.precio_unitario)}`, fmtMoneda(it.subtotal), columnas));
  });
  out.push(linea);

  if (venta.descuentoMonto > 0) {
    out.push(formatearFila('Subtotal', fmtMoneda(venta.subtotal), columnas));
    const etiquetaDescuento = venta.descuentoTipo === 'porcentaje' ? `Descuento (${venta.descuentoValor}%)` : 'Descuento';
    out.push(formatearFila(etiquetaDescuento, `-${fmtMoneda(venta.descuentoMonto)}`, columnas));
  }
  out.push(formatearFila('TOTAL', fmtMoneda(venta.total), columnas));
  if (venta.pagaCon != null && venta.pagaCon !== '') {
    out.push(formatearFila('Paga con', fmtMoneda(venta.pagaCon), columnas));
    out.push(formatearFila('Cambio', fmtMoneda(venta.cambio), columnas));
  }
  if (venta.observaciones) { out.push(''); out.push(venta.observaciones); }

  out.push('');
  if (sucursalImpresion?.impresion_texto_pie) out.push(centrar(sucursalImpresion.impresion_texto_pie));

  return out.join('\n');
}

// La impresión de una sucursal solo existe si está habilitada Y tiene una
// impresora cargada. Toda opción de imprimir en la UI se muestra únicamente
// cuando esto da true. Number() porque según el endpoint el flag puede llegar
// como número o como string ("0" sería truthy).
export function puedeImprimir(sucursalImpresion) {
  return !!Number(sucursalImpresion?.impresion_habilitada)
    && !!String(sucursalImpresion?.impresion_nombre_impresora ?? '').trim();
}

// Conecta a QZ Tray y manda el ticket ya armado (ESC/POS crudo). Lanza si algo
// falla: los llamadores lo envuelven para devolver {ok, motivo}.
async function imprimirRaw(sucursalImpresion, ticket, certificadoPublico) {
  await asegurarConexion(certificadoPublico);
  const config = qz.configs.create(sucursalImpresion.impresion_nombre_impresora);
  await qz.print(config, [{ type: 'raw', format: 'plain', data: ticket }]);
}

// Best-effort: nunca tira excepción hacia quien la llama; nunca demora el
// flujo de la venta. Si no hay impresora configurada/habilitada, no hace nada.
export async function imprimirTicketSilencioso(venta, sucursalImpresion, opcionesGlobales = {}) {
  if (!puedeImprimir(sucursalImpresion)) {
    return { ok: false, motivo: 'sin_configurar' };
  }
  try {
    await imprimirRaw(sucursalImpresion, construirTicketEscPos(venta, sucursalImpresion, opcionesGlobales), opcionesGlobales.impresion_qz_cert);
    return { ok: true };
  } catch (e) {
    console.warn('No se pudo imprimir el ticket:', e);
    return { ok: false, motivo: e?.message || String(e) };
  }
}

/**
 * Ticket del resumen de cierre de turno (parecido al mail de cierre).
 * resumen: payload de turnos/turnocerrar.php (data.resumen) o turnos/turnoresumen.php:
 *   { turno:{id, numero_turno_dia, sucursal, direccion, apertura, cierre, usuario_apertura, usuario_cierre, observaciones},
 *     productos:[{descripcion, cantidad, peso_kg, importe}], totales:{cantidad, peso_kg, bruto, descuento, neto},
 *     checklist:[{descripcion, completado, observaciones}] }
 * Solo ASCII/Latin-1 (aBytesImprimibles cambia por "?" lo que no entra). No lleva
 * el pie de la comanda ("¡Gracias por su compra!" es para clientes, no para un cierre de caja).
 */
export function construirTicketTurnoEscPos(resumen, sucursalImpresion) {
  const ESC = String.fromCharCode(0x1B);
  const GS = String.fromCharCode(0x1D);
  const init = ESC + '@';
  const codepage = ESC + 't' + String.fromCharCode(CODEPAGE_POR_MARCA[sucursalImpresion?.impresion_marca] ?? 16);
  const alinearCentro = ESC + 'a' + String.fromCharCode(1);
  const alinearIzq = ESC + 'a' + String.fromCharCode(0);
  const negritaOn = ESC + 'E' + String.fromCharCode(1);
  const negritaOff = ESC + 'E' + String.fromCharCode(0);
  const cortar = GS + 'V' + String.fromCharCode(0);

  const columnas = Number(sucursalImpresion?.impresion_ancho_papel) === 58 ? 32 : 48;
  const linea = '-'.repeat(columnas);
  const fmtKg = (n) => `${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;

  const t = resumen?.turno ?? {};
  const productos = resumen?.productos ?? [];
  const totales = resumen?.totales ?? {};
  const checklist = resumen?.checklist ?? [];

  let out = init + codepage + alinearCentro;
  if (sucursalImpresion?.impresion_nombre_fantasia) out += negritaOn + aBytesImprimibles(sucursalImpresion.impresion_nombre_fantasia) + negritaOff + '\n';
  if (t.direccion) out += aBytesImprimibles(t.direccion) + '\n';
  out += linea + '\n';
  out += negritaOn + `CIERRE DE TURNO #${t.id ?? ''}` + negritaOff + '\n';
  out += aBytesImprimibles(`${t.sucursal ?? ''}${t.numero_turno_dia ? ` - Turno ${t.numero_turno_dia}` : ''}`) + '\n';
  out += linea + '\n';

  out += alinearIzq;
  out += formatearFila('Apertura', t.apertura || '-', columnas) + '\n';
  if (t.usuario_apertura) out += formatearFila('  Abrio', t.usuario_apertura, columnas) + '\n';
  out += formatearFila('Cierre', t.cierre || '-', columnas) + '\n';
  if (t.usuario_cierre) out += formatearFila('  Cerro', t.usuario_cierre, columnas) + '\n';
  out += linea + '\n';

  out += negritaOn + 'DETALLE DE LO REGISTRADO' + negritaOff + '\n';
  if (productos.length === 0) {
    out += 'No se cargaron registros en este turno.\n';
  } else {
    productos.forEach((p) => {
      out += aBytesImprimibles(p.descripcion) + '\n';
      const izq = `  ${p.cantidad} un${Number(p.peso_kg) > 0 ? `  ${fmtKg(p.peso_kg)}` : ''}`;
      out += formatearFila(izq, fmtMoneda(p.importe), columnas) + '\n';
    });
    out += linea + '\n';
    out += formatearFila('Cantidad', String(totales.cantidad ?? 0), columnas) + '\n';
    if (Number(totales.peso_kg) > 0) out += formatearFila('Peso', fmtKg(totales.peso_kg), columnas) + '\n';
    if (Number(totales.descuento) > 0) {
      out += formatearFila('Subtotal', fmtMoneda(totales.bruto), columnas) + '\n';
      out += formatearFila('Descuentos', `-${fmtMoneda(totales.descuento)}`, columnas) + '\n';
    }
    out += negritaOn + formatearFila('TOTAL', fmtMoneda(totales.neto), columnas) + negritaOff + '\n';
  }

  if (checklist.length > 0) {
    out += linea + '\n';
    out += negritaOn + 'CHECKLIST DEL TURNO' + negritaOff + '\n';
    checklist.forEach((c) => {
      out += aBytesImprimibles(c.completado ? `[x] ${c.descripcion}` : `[ ] ${c.descripcion} - PENDIENTE`) + '\n';
      if (c.observaciones) out += aBytesImprimibles(`      ${c.observaciones}`) + '\n';
    });
  }

  if (t.observaciones) {
    out += linea + '\n';
    out += aBytesImprimibles(`Observaciones: ${t.observaciones}`) + '\n';
  }

  out += '\n\n\n';
  if (sucursalImpresion?.impresion_cortar_papel) out += cortar;

  return out;
}

// El certificado de firma de QZ Tray es global; se pide una sola vez, recién
// cuando hace falta imprimir (así Turno/TurnosList no suman una request en
// cada montaje). Si la request falla se reintenta la próxima vez y NO se
// configura la seguridad de QZ con un certificado vacío para el resto de la sesión.
let certificadoGlobalPromise = null;
function obtenerCertificadoGlobal() {
  if (!certificadoGlobalPromise) {
    certificadoGlobalPromise = axios.get(API_BASE_URL + FACTURACION_GET, { headers: authHeader() })
      .then((res) => {
        // facturacionget.php responde HTTP 200 aunque falle; sin este chequeo un
        // error transitorio se cachearía como "sin certificado" para toda la sesión.
        if (res.data?.success !== true) throw new Error(res.data?.message || 'No se pudo leer la configuración de impresión.');
        return res.data?.data?.impresion_qz_cert || '';
      })
      .catch((e) => { certificadoGlobalPromise = null; throw e; });
  }
  return certificadoGlobalPromise;
}

// Imprime el resumen de cierre de un turno. Best-effort como imprimirTicketSilencioso:
// nunca tira excepción; devuelve {ok:true} o {ok:false, motivo}.
export async function imprimirResumenTurnoSilencioso(resumen) {
  const sucursalImpresion = resumen?.sucursal_impresion;
  if (!puedeImprimir(sucursalImpresion)) {
    return { ok: false, motivo: 'sin_configurar' };
  }
  try {
    const certificado = await obtenerCertificadoGlobal();
    await imprimirRaw(sucursalImpresion, construirTicketTurnoEscPos(resumen, sucursalImpresion), certificado);
    return { ok: true };
  } catch (e) {
    console.warn('No se pudo imprimir el resumen del turno:', e);
    return { ok: false, motivo: e?.message || String(e) };
  }
}
